import Vue from 'vue';
import VueRouter from 'vue-router';
import MapLanding from '@/views/MapLanding.vue';
import { TargetAudiences } from '@/store/types';

Vue.use(VueRouter);

export enum RouterAliases {
  AUTH_CALLBACK = 'AUTH_CALLBACK',
  CONTACT = 'contact',
  DEFAULT_LANDING = 'DEFAULT_LANDING',
  DOCTOR_LANDING = 'DOCTOR_LANDING',
  FAQ = 'faq',
  FLOW_PICKER = 'FLOW_PICKER',
  HOSPITAL_DETAILS = 'hospital_details',
  HOSPITAL_REPORT = 'hospital_report',
  HOSPITAL_SEARCH = 'hospital_search',
  PATIENT_ASSESSMENT = 'PATIENT_ASSESSMENT',
  PATIENT_ASSESSMENT_RESULTS = 'PATIENT_ASSESSMENT_RESULTS',
  DOCTOR_ASSESSMENT = 'DOCTOR_ASSESSMENT',
  DOCTOR_ASSESSMENT_RESULTS = 'DOCTOR_ASSESSMENT_RESULTS',
  PATIENT_HOME = 'PATIENT_HOME',
  REPORT_CONFIRMATION = 'REPORT_CONFIRMATION',
  REPORT_GENERAL_STATUS = 'REPORT_GENERAL_STATUS',
  REPORT_VALIDATION = 'report_validation',
  REPORTING_LANDING = 'reporting_landing',
  THANK_YOU = 'THANK_YOU',
}

const routes = [
  {
    path: '/',
    name: RouterAliases.DEFAULT_LANDING,
    props: {
      targetAudience: process.env.VUE_APP_FLOW,
    },
    component: MapLanding,
  },
  {
    path: '/callback',
    name: RouterAliases.AUTH_CALLBACK,
    component: () =>
      import(/* decision-maker-component*/ '@/views/Auth0Callback.vue'),
  },
  {
    path: '/contact',
    name: RouterAliases.CONTACT,
    component: () => import(/* contact-component */ '@/views/Contact.vue'),
  },
  {
    path: '/doctors',
    name: RouterAliases.DOCTOR_LANDING,
    component: MapLanding,
    props: {
      targetAudience: TargetAudiences.DOCTOR,
    },
  },
  { path: '/doctors/assessment', redirect: '/doctors/assessment/1' },
  {
    path: '/doctors/assessment/results',
    name: RouterAliases.DOCTOR_ASSESSMENT_RESULTS,
    props: (route: any) => ({
      address: route.query.address || null,
      audience: TargetAudiences.DOCTOR,
      lat: parseFloat(route.query.lat),
      lng: parseFloat(route.query.lng),
      resources: route.query.resources ? route.query.resources.split(',') : [],
    }),
    component: () =>
      import(/* patient-care-results-component */ '@/views/ResultsMap.vue'),
    beforeEnter: (to: any, from: any, next: any) => {
      const { query } = to;
      if (!(query.lat && query.lng)) {
        next({
          name: RouterAliases.DOCTOR_ASSESSMENT,
          params: {
            step: 1,
          },
        });
      } else {
        next();
      }
    },
  },
  {
    path: '/doctors/assessment/:step',
    name: RouterAliases.DOCTOR_ASSESSMENT,
    props: (route: any) => ({
      currentStep: parseInt(route.params.step, 10) || 1,
    }),
    component: () =>
      import(/* patient-care-component */ '@/views/PatientCare.vue'),
  },
  {
    path: '/faq',
    name: RouterAliases.FAQ,
    component: () => import(/* faq-component */ '@/views/FAQ.vue'),
  },
  {
    path: '/hospital-details/:hospitalId/:visitor?',
    props: true,
    name: RouterAliases.HOSPITAL_DETAILS,
    component: () =>
      import(/* hospital-details-component*/ '@/views/HospitalDetails.vue'),
  },
  {
    path: '/hospital-report/:hospitalId/:step',
    name: RouterAliases.HOSPITAL_REPORT,
    component: () =>
      import(/* hospital-report-component*/ '@/views/ReportWizard.vue'),
  },
  {
    path: '/hospital-report-validation/:hospitalId',
    props: true,
    name: RouterAliases.REPORT_VALIDATION,
    component: () =>
      import(
        /* hospital-report-validation-component*/ '@/views/ReportValidation.vue'
      ),
  },
  {
    path: '/hospital-search',
    name: RouterAliases.HOSPITAL_SEARCH,
    component: () =>
      import(/* hospital-search-component*/ '@/views/HospitalSearch.vue'),
  },
  { path: '/patients/assessment', redirect: '/patients/assessment/1' },
  {
    path: '/patients/assessment/results',
    name: RouterAliases.PATIENT_ASSESSMENT_RESULTS,
    props: (route: any) => ({
      address: route.query.address || null,
      age: route.query.age || null,
      audience: TargetAudiences.PATIENT,
      condition: route.query.condition
        ? parseFloat(route.query.condition)
        : null,
      gender: route.query.gender || null,
      lat: parseFloat(route.query.lat),
      lng: parseFloat(route.query.lng),
      resources: route.query.resources ? route.query.resources.split(',') : [],
    }),
    component: () =>
      import(/* patient-care-results-component */ '@/views/ResultsMap.vue'),
    beforeEnter: (to: any, from: any, next: any) => {
      const { query } = to;
      if (!(query.lat && query.lng)) {
        next({
          name: RouterAliases.PATIENT_ASSESSMENT,
          params: {
            step: 1,
          },
        });
      } else {
        next();
      }
    },
  },
  {
    path: '/patients/assessment/:step',
    name: RouterAliases.PATIENT_ASSESSMENT,
    component: () =>
      import(
        /* patient-assessment-component */ '@/views/PatientAssessment.vue'
      ),
    props: (route: any) => ({
      currentStep: parseInt(route.params.step, 10) || 1,
    }),
  },
  {
    path: '/picker',
    name: RouterAliases.FLOW_PICKER,
    component: () =>
      import(/* flow-picker-component */ '@/views/FlowPicker.vue'),
  },
  { path: '/report', redirect: '/reporting' },
  {
    path: '/reporting',
    name: RouterAliases.REPORTING_LANDING,
    component: MapLanding,
    props: {
      targetAudience: TargetAudiences.REPORTING,
    },
  },
  {
    path: '/reporting/general/:hospitalId',
    props: true,
    name: RouterAliases.REPORT_GENERAL_STATUS,
    component: () =>
      import(/* hospital-report-general */ '@/views/HospitalReportGeneral.vue'),
  },
  {
    path: '/reporting/confirmation',
    name: RouterAliases.REPORT_CONFIRMATION,
    component: () =>
      import(
        /* hospital-report-confirmation */ '@/views/ReportConfirmation.vue'
      ),
    props: (route: any) => ({
      reportId: route.query.report,
      reporter: route.query.reporter
        ? parseInt(route.query.reporter, 10)
        : null,
    }),
    beforeEnter: (to: any, from: any, next: any) => {
      const { query } = to;
      if (!query.report) {
        next({
          name: RouterAliases.HOSPITAL_SEARCH,
        });
      } else {
        next();
      }
    },
  },
  {
    path: '/thank-you/:hospitalId',
    props: true,
    name: RouterAliases.THANK_YOU,
    component: () =>
      import(/* hospital-report-thank-you*/ '@/views/ThankYou.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

export function useRouter(): VueRouter {
  return router;
}

router.beforeEach(async (to, from, next) => {
  if (process.env.VUE_APP_AUTH0_ENABLED) {
    const auth = await import('@/util/auth');
    if (auth.isLoggedIn() || to.name === RouterAliases.AUTH_CALLBACK) {
      return next();
    } else {
      return auth.login();
    }
  }
  return next();
});

export default router;
